import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, ElementRef, inject, Inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { AuthService, LOGIN_CODE_TOKEN_LENGTH } from '@core/auth';
import { AmplitudeEventService, FeatureFlagService, ProviderSettingsService } from '@core/services';
import { LoggedIn } from '@integrations/amplitude';
import { TranslateService } from '@ngx-translate/core';
import { ProviderGeneralData } from '@shared/models';
import { ToastService, DialogService } from '@shared/services';
import { fbString, showSpinner, TypedRoute } from '@shared/utils';
import { LoginService } from './login.service';
import { switchMap } from 'rxjs';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit {
  destroyRef = inject(DestroyRef);
  @ViewChild('emailInput') emailInput!: ElementRef;

  emailSent: boolean = false;
  email?: string;

  showDemoLogin: boolean = false;
  showGoogleLogin: boolean = false;
  showOnlyDemoLogin: boolean = false;
  signUpEnabled: boolean = false;
  providerData?: ProviderGeneralData;
  logInForm = this.formBuilder.nonNullable.group({
    email: [fbString, [Validators.required, Validators.email]],
  });

  constructor(
    @Inject(ActivatedRoute) private route: TypedRoute<{ token: string }>,
    private authService: AuthService,
    private formBuilder: FormBuilder,
    private loginService: LoginService,
    private toastService: ToastService,
    private translateService: TranslateService,
    private featureFlagService: FeatureFlagService,
    private amplitudeEventService: AmplitudeEventService,
    private providerSettingsService: ProviderSettingsService,
    private dialogService: DialogService,
  ) {

    if (this.route.snapshot.queryParamMap.get('checkAuthStatus') === 'true') {
      this.authService.checkAuthStatus().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (payload) => {
          if (payload.meta.is_authenticated && payload.meta.session_token) {
            this.authService.logIn(payload.meta.session_token);
          }
        },
        error: (error: HttpErrorResponse) => {
          if (error.status === 401 && this.authService.isEmailVerificationPending(error?.error)) {
            this.toastService.add({
              message: this.translateService.instant('LogIn.VerifyYourEmailToLogIn'),
              type: 'success',
            });
          } else {
            this.authService.showTokenVerifyErrorToast(error);
          }
        },
      });
    }

    this.route.data.subscribe((data) => {
      if (data.token) {
        if (data.token.length > LOGIN_CODE_TOKEN_LENGTH) {
          // TODO delete me after some time - only needed to not break old login links until they are expired
          // DEPRECATED
          const redirectPath = this.route.snapshot.queryParamMap.get('redirect') || undefined;
          if (!redirectPath) {
            this.dialogService.addDialog({
              title: this.translateService.instant('Dialog.OldActivationLinkIncorrect.Title'),
              content: this.translateService.instant('Dialog.OldActivationLinkIncorrect.Content'),
              buttonText: this.translateService.instant('Dialog.Understood'),
              buttonRoute: { link: '/login' },
              type: 'error',
            });
            return;
          }
          this.authService
            .logOutWithoutRedirect()
            .pipe(switchMap(() => this.authService.loginDeprecatedJWT(data.token)))
            .pipe(showSpinner())
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: () => {
                this.dialogService.addDialog({
                  title: this.translateService.instant('Dialog.NewActivationLinkWasSent.Title'),
                  content: this.translateService.instant('Dialog.NewActivationLinkWasSent.Content'),
                  buttonText: this.translateService.instant('Dialog.Understood'),
                  buttonRoute: { link: '/login' },
                  type: 'info'
                });
              },
              error: () => {
                this.dialogService.addDialog({
                  title: this.translateService.instant('Dialog.OldActivationLinkIncorrect.Title'),
                  content: this.translateService.instant('Dialog.OldActivationLinkIncorrect.Content'),
                  buttonText: this.translateService.instant('Dialog.Understood'),
                  buttonRoute: { link: '/login' },
                  type: 'error',
                });
              },
            });
        } else {
          this.authService
            .verifyLoginJWT(data.token)
            .pipe(showSpinner())
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: (data) => {
                const redirectPath = this.route.snapshot.queryParamMap.get('redirect');
                this.amplitudeEventService.trackEvent(new LoggedIn());
                this.authService.logIn(data.meta.session_token, redirectPath ?? undefined);
              },
              error: (error: HttpErrorResponse) => {
                let message = undefined;
                if (error.status === 400) {
                  message = 'LogIn.Errors.LoginCodeIncorrect';
                } else if (error.status === 401 && this.authService.isEmailVerificationPending(error?.error)) {
                  this.toastService.add({
                    message: this.translateService.instant('LogIn.VerifyYourEmailToLogIn'),
                    type: 'success',
                  });
                  return;
                }
                this.authService.showTokenVerifyErrorToast(error, message);
              },
            });
        }
      }
    });
  }

  ngOnInit(): void {
    this.showDemoLogin = this.featureFlagService.getFeatureFlag('dev_enable_demo_login');
    this.showOnlyDemoLogin = this.featureFlagService.getFeatureFlag('dev_enable_demo_login_only');
    this.signUpEnabled = this.featureFlagService.getFeatureFlag('enable_signup');
    this.showGoogleLogin = this.featureFlagService.getFeatureFlag('enable_google_login');
    this.providerData = this.providerSettingsService.getProviderGeneralData();
  }


  sendLogInEmail() {
    this.logInForm.markAllAsTouched();
    if (this.logInForm.valid) {
      this.email = this.logInForm.value.email!;
      this.loginService
        .sendLoginEmail(this.email)
        .pipe(showSpinner())
        .subscribe({
          next: () => {
            this.emailSent = true;
            this.toastService.add({
              message: this.translateService.instant('LogIn.WeHaveSentAnEmailToast', { email: this.email }),
              type: 'success',
            });
          },
          error: (error: HttpErrorResponse) => {
            if (error.status === 429) {
              this.toastService.add({
                message: error.error.message ?? this.translateService.instant('Common.Errors.UnknownError'),
                type: 'error',
              });
            } else if (error.status === 401 && this.authService.isLoginByCodeVerificationPending(error?.error)) {
              this.emailSent = true;
              this.toastService.add({
                message: this.translateService.instant('LogIn.WeHaveSentAnEmailToast', { email: this.email }),
                type: 'success',
              });
            } else {
              this.toastService.add({
                message: this.translateService.instant('LogIn.CouldNotSendEmail'),
                type: 'error',
              });
            }
          },
        });
    }
  }

  logIn(): void {
    this.authService.logIn('demotoken');
  }

  logInWithGoogle(): void {
    this.authService.logOutWithoutRedirect()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.authService.logInWithGoogle();
        },
        error: () => {
          this.authService.logInWithGoogle();
        }
      });
  }
}
